<template>
  <div v-if="reservationRequests.length > 0" class="mb-10">
    <h1 class="text-h1 mb-16">Out-of-Network care</h1>
    <Card
      v-for="reservation in reservationRequests"
      :key="reservation.bhReservationRequestId"
      class="reservation-card w-full mb-24"
      :clickable="false"
    >
      <h3 class="reservation-card-date text-h4 mb-8">{{ findAndFormatDateRange(reservation.scCareSessions) }} reservation</h3>
      <p class="reservation-card-time text-s text-grey-body mb-8">
        {{ findTimeText(reservation.scCareSessions) }}
      </p>
      <p class="text-s text-grey-body mb-8">
        Out-of-Network Care • ${{ formatReimbursementAmount(reservation.reimbursement.amount) }} estimated reimbursement
      </p>
      <Card class="w-full hover:box-shadow-none" :clickable="false" :elevation="1">
        <h3 class="text-h4 mb-8">Next steps</h3>
        <p class="text-s text-grey-body mb-20">Find care and use your Back-Up Care benefit</p>
        <StepCardList :items="createItemsList(reservation.scCareSessions)" />
      </Card>
    </Card>
  </div>
</template>

<script lang="ts">
import { format, max, min, parseISO } from 'date-fns';

import Card from '@/components/card/Card.vue';
import StepCardList from '@/components/step_card_list/StepCardList.vue';
import OutOfNetworkPendingReimbursementPopup from '@/apps/account/seeker/OutOfNetworkPendingReimbursementPopup.vue';

import { BH_RESERVATION_REQUESTS } from '@/graphql/backup_care/queries';

export default {
  name: 'OutOfNetworkCareModule',
  components: {
    Card,
    StepCardList,
  },
  data(): any {
    return {
      reservationRequests: [],
    };
  },
  mounted() {
    this.fetchBhReservationRequests();
  },
  methods: {
    findAndFormatDateRange(sessions) {
      const sessionStartDates: [Date] = sessions.map((session) => new Date(session.start));
      const earliestSessionDate: Date = min(sessionStartDates);
      const latestSessionDate: Date = max(sessionStartDates);

      const earliestFormatted: string = format(earliestSessionDate, 'MMMM d, yyyy');
      const latestFormatted: string = format(latestSessionDate, 'MMMM d, yyyy');

      const [earliestMonth, latestMonth] = [earliestSessionDate, latestSessionDate].map((date) => format(date, 'MMMM'));
      const [earliestDay, latestDay] = [earliestSessionDate, latestSessionDate].map((date) => format(date, 'd'));
      const [earliestYear, latestYear] = [earliestSessionDate, latestSessionDate].map((date) => format(date, 'yyyy'));

      if (earliestMonth === latestMonth && earliestDay === latestDay && earliestYear === latestYear) {
        return earliestFormatted;
      }
      if (earliestMonth === latestMonth && earliestYear === latestYear) {
        return `${earliestMonth} ${earliestDay}-${latestDay}, ${earliestYear}`;
      }
      // if the years are different, for example in the case of a session that starts in December
      // and lasts through until January
      return `${earliestFormatted} - ${latestFormatted}`;
    },
    formatTime(start, end): string {
      const startDateFormatted: string = format(new Date(start), 'h:mm a');
      const endDateFormatted: string = format(new Date(end), 'h:mm a');
      const timeZone: string =
        new Intl.DateTimeFormat('en-US', {
          timeZoneName: 'short',
        })
          ?.formatToParts(new Date(start))
          ?.find((part) => part.type === 'timeZoneName')?.value || '';

      return `${startDateFormatted} – ${endDateFormatted} ${timeZone}`;
    },
    findTimeText(sessions) {
      const startTime = format(parseISO(sessions[0].start), 'HH:mm:ss');
      const endTime = format(parseISO(sessions[0].end), 'HH:mm:ss');

      const hasMultipleStartTimes: boolean = sessions.some((session) => {
        return format(parseISO(session.start), 'HH:mm:ss') !== startTime;
      });
      const hasMultipleEndTimes: boolean = sessions.some((session) => {
        return format(parseISO(session.end), 'HH:mm:ss') !== endTime;
      });

      if (hasMultipleStartTimes || hasMultipleEndTimes) {
        return 'Multiple times';
      }
      return this.formatTime(sessions[0].start, sessions[0].end);
    },
    formatReimbursementAmount(rate): string {
      // formats a number to show decimals only if needed
      // checks if the number is whole.
      // If whole, show no decimals. If not whole, it shows 2 decimals.
      const reimbursementNumber = Number(rate);
      return reimbursementNumber % 1 === 0 ? reimbursementNumber.toFixed(0) : reimbursementNumber.toFixed(2);
    },
    requestCaregiverListItem(session) {
      const getBaseItem = () => ({
        variant: 'incomplete',
        title: `Request a caregiver for your ${format(new Date(session.start), 'MMMM d, yyyy')} care session`,
        content: session.request?.contract?.sitter
          ? `${session.request.contract.sitter.firstName} ${session.request.contract.sitter.lastInitial}. accepted your care request`
          : 'Find a caregiver on Sittercity or invite your own',
        linkURL: `/backup-care/${session.id}/new/invite`,
        disabled: false,
      });

      const getLockedItem = () => ({
        ...getBaseItem(),
        variant: 'pending',
        content: 'This will unlock 180 days before your care session',
        linkURL: '',
        disabled: true,
      });

      const getCanceledItem = () => ({
        ...getBaseItem(),
        variant: 'warning',
        content: 'Your previous booking request was canceled',
      });

      const getAcceptedItem = () => ({
        ...getBaseItem(),
        variant: 'complete',
        linkURL: `/bookings/${session.request?.contract?.id}`,
        disabled: true,
      });

      const getExpiredItem = () => ({
        ...getBaseItem(),
        variant: 'warning',
        content: `This step expired on ${format(new Date(session.request.expiry), 'MMMM d, yyyy')}`,
        linkURL: '',
        disabled: true,
      });

      const getWaitingForResponseItem = () => ({
        ...getBaseItem(),
        variant: 'pending',
        title: `Waiting for a response to your ${format(new Date(session.start), 'MMMM d, yyyy')} request`,
        content: `Your caregiver must accept before ${format(new Date(session.request.expiry), 'MMMM d, yyyy')}`,
        linkURL: `/requests/${session.request?.id}`,
      });

      const { bookingState } = session;

      switch (bookingState) {
        case 'LOCKED':
          return getLockedItem();
        case 'CANCELLED':
          return getCanceledItem();
        case 'ACCEPTED_CANCELLED':
          return getCanceledItem();
        case 'ACCEPTED':
          return getAcceptedItem();
        case 'EXPIRED':
          return getExpiredItem();
        case 'PENDING':
          return getWaitingForResponseItem();
        case 'DECLINED':
          return getCanceledItem();
        default:
          return getBaseItem();
      }
    },
    submitReimbursementListItem(sessions) {
      const lastCareSessionDate = new Date(sessions[sessions.length - 1].end);
      const currentDate = new Date();

      if (lastCareSessionDate < currentDate) {
        return {
          variant: 'newTab',
          title: 'Submit reimbursement request',
          content: 'Return to Back-Up Care to submit your reimbursement request',
          linkURL: 'https://backup.brighthorizons.com/',
        };
      }
      return {
        variant: 'pending',
        title: 'Submit reimbursement request',
        content: 'This will unlock after your last care session',
        disabled: true,
        modalComponent: OutOfNetworkPendingReimbursementPopup,
      };
    },
    createItemsList(sessions) {
      const caregiverListItems = sessions.map((session) => this.requestCaregiverListItem(session));
      const reimbursementListItem = this.submitReimbursementListItem(sessions);
      return [...caregiverListItems, reimbursementListItem];
    },
    fetchBhReservationRequests(): void {
      this.$apollo
        .query({
          query: BH_RESERVATION_REQUESTS,
        })
        .then(({ data }) => {
          this.reservationRequests = data.corp.backupCare.bhReservationRequests.filter(
            (reservations) => reservations.scCareSessions.length
          );
        })
        .catch((error) => {
          console.error(error); // eslint-disable-line no-console
        });
    },
  },
};
</script>
